import { z } from 'zod';

const pageSchema = z.number().int().positive().catch(1).default(1);
const searchTextSchema = z.string().catch('').default('');
const stringArraySchema = z.array(z.string()).catch([]).default([]);
const discussionSchema = z.boolean().catch(false).default(false);
const VOTE_TYPES = ['upvote', 'downvote'] as const;
const voteTypesSchema = z
  .array(z.enum(VOTE_TYPES))
  .max(VOTE_TYPES.length)
  .catch([])
  .default([]);

export const proactiveRoutinesSearchSchema = z.object({
  section: z
    .enum(['all', 'production', 'experimental'])
    .catch('all')
    .default('all'),
});
export const PROACTIVE_ROUTINES_SEARCH_DEFAULTS =
  proactiveRoutinesSearchSchema.parse({});

export const feedbackSearchSchema = z.object({
  page: pageSchema,
});
export const FEEDBACK_SEARCH_DEFAULTS = feedbackSearchSchema.parse({});

export const messageVotesSearchSchema = z.object({
  page: pageSchema,
  search: searchTextSchema,
  voteTypes: voteTypesSchema,
  models: stringArraySchema,
});
export const MESSAGE_VOTES_SEARCH_DEFAULTS = messageVotesSearchSchema.parse({});

export const messageVoteDetailsSearchSchema = z.object({
  returnTo: messageVotesSearchSchema.nullable().catch(null).default(null),
  discussion: discussionSchema,
});
export const MESSAGE_VOTE_DETAILS_SEARCH_DEFAULTS =
  messageVoteDetailsSearchSchema.parse({});

function nextHourMark(date: Date): Date {
  const nextHour = new Date(date);
  nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
  return nextHour;
}

export function addDays(date: Date, days: number): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

export function toDateTimeLocalValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

const defaultVoteAggregatesEnd = nextHourMark(new Date());
const defaultVoteAggregatesStart = addDays(defaultVoteAggregatesEnd, -7);
const localDateTimeSchema = z.string().datetime({ local: true });

export const voteAggregatesSearchSchema = z.object({
  start: localDateTimeSchema
    .catch(toDateTimeLocalValue(defaultVoteAggregatesStart))
    .default(toDateTimeLocalValue(defaultVoteAggregatesStart)),
  end: localDateTimeSchema
    .catch(toDateTimeLocalValue(defaultVoteAggregatesEnd))
    .default(toDateTimeLocalValue(defaultVoteAggregatesEnd)),
});
export const VOTE_AGGREGATES_SEARCH_DEFAULTS = voteAggregatesSearchSchema.parse(
  {}
);

export const entityTypeSchema = z.enum([
  'meeting',
  'meeting_prep',
  'meeting_assist',
  'routine_report',
  'im_channel_proactive_routine',
  'action_card',
]);
export const entityVotesSearchSchema = z.object({
  page: pageSchema,
  search: searchTextSchema,
  voteTypes: voteTypesSchema,
  entityType: entityTypeSchema.catch('meeting').default('meeting'),
});
export const ENTITY_VOTES_SEARCH_DEFAULTS = entityVotesSearchSchema.parse({});

export const entityVoteDetailsSearchSchema = z.object({
  returnTo: entityVotesSearchSchema.nullable().catch(null).default(null),
  discussion: discussionSchema,
});
export const ENTITY_VOTE_DETAILS_SEARCH_DEFAULTS =
  entityVoteDetailsSearchSchema.parse({});

export const usersSearchSchema = z.object({
  page: pageSchema,
  search: searchTextSchema,
  plans: stringArraySchema,
  professions: stringArraySchema,
  sortBy: z
    .enum([
      'signup_date',
      'email',
      'name',
      'plan',
      'total_chats',
      'total_routine_templates',
      'total_journals',
    ])
    .catch('signup_date')
    .default('signup_date'),
  sortDir: z.enum(['asc', 'desc']).catch('desc').default('desc'),
});
export const USERS_SEARCH_DEFAULTS = usersSearchSchema.parse({});

const ledgerSourceSchema = z.enum([
  'purchase',
  'debit',
  'expiry',
  'admin',
  'team_auto_draw',
]);
const ledgerFeatureSchema = z.enum([
  'chat',
  'routine_run',
  'routine_run_lite',
  'routine_run_max',
  'routine_run_ultra',
  'meeting_prep',
  'meeting_assist',
  'journal',
  'external_user_context_search',
  'voice',
]);
const walletDateRangeSchema = z
  .union([z.literal(0), z.literal(7), z.literal(30), z.literal(90)])
  .catch(0)
  .default(0);
const walletMinAmountSchema = z.number().nonnegative().catch(0).default(0);

export const userEditSearchSchema = z.object({
  returnTo: usersSearchSchema.nullable().catch(null).default(null),
  walletPage: pageSchema,
  walletSources: z.array(ledgerSourceSchema).catch([]).default([]),
  walletFeatures: z.array(ledgerFeatureSchema).catch([]).default([]),
  walletDateRange: walletDateRangeSchema,
  walletMinAmount: walletMinAmountSchema,
});
export const USER_EDIT_SEARCH_DEFAULTS = userEditSearchSchema.parse({});

export const analysisSearchSchema = z.object({
  page: pageSchema,
  status: z
    .enum(['', 'pending', 'running', 'completed', 'failed'])
    .catch('')
    .default(''),
});
export const ANALYSIS_SEARCH_DEFAULTS = analysisSearchSchema.parse({});

export const analysisNewSearchSchema = z.object({
  returnTo: analysisSearchSchema.nullable().catch(null).default(null),
});
export const ANALYSIS_NEW_SEARCH_DEFAULTS = analysisNewSearchSchema.parse({});

export const analysisDetailSearchSchema = z.object({
  returnTo: analysisSearchSchema.nullable().catch(null).default(null),
  resultsPage: pageSchema,
});
export const ANALYSIS_DETAIL_SEARCH_DEFAULTS = analysisDetailSearchSchema.parse(
  {}
);

export const costAnalyticsSearchSchema = z.object({
  tier: z.enum(['all', 'max', 'regular', 'quick']).catch('all').default('all'),
  agents: stringArraySchema,
  sample: z
    .enum(['all', 'p10', 'p50', 'p90', 'p99', 'p99.9', 'p100'])
    .catch('all')
    .default('all'),
  days: z
    .union([z.literal(1), z.literal(7), z.literal(30), z.literal('all')])
    .catch(30)
    .default(30),
});
export const COST_ANALYTICS_SEARCH_DEFAULTS = costAnalyticsSearchSchema.parse(
  {}
);

export const agentConfigsSearchSchema = z.object({
  base: z.string().catch('').default(''),
  compare: z.string().catch('').default(''),
  metric: z.string().catch('').default(''),
  max_questions: z.coerce.number().int().min(0).catch(10).default(10),
});
export const AGENT_CONFIGS_SEARCH_DEFAULTS = agentConfigsSearchSchema.parse({});

// Findings from the last seven days by default, since the agent comparison scans
// every evaluated chat in the window and all time is slow
const defaultOnlineEvalCreatedAfter = toDateTimeLocalValue(
  addDays(nextHourMark(new Date()), -7)
);

export const onlineEvalIssuesSearchSchema = z.object({
  page: pageSchema,
  categoryIds: stringArraySchema,
  user: searchTextSchema,
  agents: stringArraySchema,
  models: stringArraySchema,
  createdAfter: z
    .string()
    .catch(defaultOnlineEvalCreatedAfter)
    .default(defaultOnlineEvalCreatedAfter),
  createdBefore: searchTextSchema,
  search: searchTextSchema,
  minConfidence: searchTextSchema,
  evaluator: searchTextSchema,
  issueCategoriesVersionId: searchTextSchema,
  debugSkeletonIssueId: searchTextSchema,
  agentStrip: searchTextSchema,
  chartUnit: z.enum(['chat', 'message']).catch('message').default('message'),
});
export const ONLINE_EVAL_ISSUES_SEARCH_DEFAULTS =
  onlineEvalIssuesSearchSchema.parse({});

export const numericIdSchema = z.coerce.number().int().positive();

export type UsersSearch = z.infer<typeof usersSearchSchema>;
